/**
 * Router — All top-level routes with lazy loading.
 * v4.8 Spec Section 3: Every page lazy-loaded, error boundaries on each.
 *
 * Structure:
 *   /auth/*          — Public auth routes
 *   /                — Protected app routes (wrapped in AppLayout)
 *   /*               — 404 catch-all
 */

import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/components/layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { SkeletonPage } from '@/components/shared/SkeletonPage';

// ─────────────────────────────────────────────────────────────────────────────
// Lazy imports — code-split per page
// ─────────────────────────────────────────────────────────────────────────────

// Auth
const Login = lazy(() => import('@/pages/Login').then((m) => ({ default: m.Login })));
const AuthCallback = lazy(() => import('@/pages/AuthCallback').then((m) => ({ default: m.AuthCallback })));
const ResetPassword = lazy(() => import('@/pages/ResetPassword').then((m) => ({ default: m.ResetPassword })));

// Command
const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })));

// Placeholder factory — lazy wrapped
const Placeholder = lazy(() => import('@/pages/PlaceholderPage').then((m) => ({ default: m.PlaceholderPage })));

// ─────────────────────────────────────────────────────────────────────────────
// Suspense wrapper — shows skeleton during lazy load
// ─────────────────────────────────────────────────────────────────────────────

function LazyPage({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <ErrorBoundary>
      <Suspense fallback={<SkeletonPage />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Placeholder pages — all Phase 2+ routes
// ─────────────────────────────────────────────────────────────────────────────

function P({ title, group }: { title: string; group: string }): React.ReactElement {
  return (
    <LazyPage>
      <Placeholder title={title} group={group} />
    </LazyPage>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Router
// ─────────────────────────────────────────────────────────────────────────────

export const router = createBrowserRouter([
  // ─── Public: Auth routes ──────────────────────────────────────────
  {
    path: '/auth/login',
    element: <LazyPage><Login /></LazyPage>,
  },
  {
    path: '/auth/callback',
    element: <LazyPage><AuthCallback /></LazyPage>,
  },
  {
    path: '/auth/reset-password',
    element: <LazyPage><ResetPassword /></LazyPage>,
  },

  // ─── Protected: App routes ────────────────────────────────────────
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // COMMAND
          {
            path: '/',
            element: <LazyPage><Dashboard /></LazyPage>,
          },
          {
            path: '/focus-brief',
            element: <P title="Focus Brief" group="Command" />,
          },
          {
            path: '/calendar',
            element: <P title="Intelligence Calendar" group="Command" />,
          },

          // PIPELINE
          {
            path: '/pipeline/acquisitions',
            element: <P title="Acquisition Register" group="Pipeline" />,
          },
          {
            path: '/pipeline/due-diligence',
            element: <P title="Due Diligence Hub" group="Pipeline" />,
          },
          {
            path: '/pipeline/feasibility',
            element: <P title="Feasibility" group="Pipeline" />,
          },
          {
            path: '/pipeline/pre-sales',
            element: <P title="Pre-Sales" group="Pipeline" />,
          },
          {
            path: '/pipeline/finance',
            element: <P title="Development Finance" group="Pipeline" />,
          },

          // PROJECTS
          {
            path: '/projects',
            element: <P title="Projects" group="Projects" />,
          },
          {
            path: '/projects/:projectId',
            element: <P title="Project Workspace" group="Projects" />,
          },

          // FINANCE
          {
            path: '/finance/budget',
            element: <P title="Budget & Costs" group="Finance" />,
          },
          {
            path: '/finance/invoices',
            element: <P title="Invoices" group="Finance" />,
          },
          {
            path: '/finance/claims',
            element: <P title="Progress Claims" group="Finance" />,
          },
          {
            path: '/finance/contracts',
            element: <P title="Contracts" group="Finance" />,
          },
          {
            path: '/finance/variations',
            element: <P title="Variations" group="Finance" />,
          },
          {
            path: '/finance/tenders',
            element: <P title="Tenders" group="Finance" />,
          },
          {
            path: '/finance/retention',
            element: <P title="Retention" group="Finance" />,
          },

          // SITE & HANDOVER
          {
            path: '/site/observations',
            element: <P title="Site Observations" group="Site & Handover" />,
          },
          {
            path: '/site/defects',
            element: <P title="Defects" group="Site & Handover" />,
          },
          {
            path: '/site/voice-notes',
            element: <P title="Voice Notes" group="Site & Handover" />,
          },
          {
            path: '/site/rfi',
            element: <P title="RFI Register" group="Site & Handover" />,
          },
          {
            path: '/site/sales',
            element: <P title="Sales & Units" group="Site & Handover" />,
          },
          {
            path: '/site/buyers',
            element: <P title="Buyers" group="Site & Handover" />,
          },

          // DOCUMENTS
          {
            path: '/documents',
            element: <P title="Document Library" group="Documents" />,
          },
          {
            path: '/documents/templates',
            element: <P title="Templates" group="Documents" />,
          },

          // CONTACTS
          {
            path: '/contacts',
            element: <P title="Contacts" group="Contacts" />,
          },
          {
            path: '/contacts/consultants',
            element: <P title="Consultants" group="Contacts" />,
          },
          {
            path: '/contacts/contractors',
            element: <P title="Contractors" group="Contacts" />,
          },

          // INTELLIGENCE
          {
            path: '/intelligence/assistant',
            element: <P title="AI Assistant" group="Intelligence" />,
          },
          {
            path: '/intelligence/stage-prompts',
            element: <P title="Stage Prompts" group="Intelligence" />,
          },
          {
            path: '/intelligence/learnings',
            element: <P title="Learnings" group="Intelligence" />,
          },
          {
            path: '/intelligence/minutes',
            element: <P title="Meeting Minutes AI" group="Intelligence" />,
          },

          // REPORTING
          {
            path: '/reporting/pcg',
            element: <P title="PCG Studio" group="Reporting" />,
          },
          {
            path: '/reporting/custom',
            element: <P title="Custom Reports" group="Reporting" />,
          },
          {
            path: '/reporting/investor',
            element: <P title="Investor Updates" group="Reporting" />,
          },

          // COMMUNICATIONS
          {
            path: '/comms/notifications',
            element: <P title="Notifications" group="Communications" />,
          },
          {
            path: '/comms/broadcasts',
            element: <P title="Broadcasts" group="Communications" />,
          },

          // SYSTEM
          {
            path: '/system/help',
            element: <P title="Help Centre" group="System" />,
          },
          {
            path: '/system/features',
            element: <P title="Feature Control" group="System" />,
          },
          {
            path: '/system/admin',
            element: <P title="Admin Panel" group="System" />,
          },
          {
            path: '/system/connectors',
            element: <P title="Connectors" group="System" />,
          },

          // Mobile "More" menu
          {
            path: '/menu',
            element: <P title="Menu" group="Navigation" />,
          },
        ],
      },
    ],
  },

  // ─── 404 catch-all ────────────────────────────────────────────────
  {
    path: '*',
    element: (
      <div className="flex items-center justify-center min-h-screen bg-surface-page dark:bg-dark-page">
        <div className="glass-card p-8 text-center max-w-md animate-spring">
          <h1 className="text-4xl font-bold text-text-primary dark:text-text-dark-primary mb-2">404</h1>
          <p className="text-text-secondary dark:text-text-dark-secondary text-sm mb-4">
            Page not found.
          </p>
          <a href="/" className="btn-gold inline-block">
            Back to Dashboard
          </a>
        </div>
      </div>
    ),
  },
]);
